/* ============================================================
   sc-studio.js
   Sofa Studio — hydration layer for the personalised portal.

   The Studio sections render server-side with no knowledge of the
   customer, then this fills them in from SC.saves. Same pattern as
   sc-gallery-personal.liquid: markup ships hidden, JS reveals it.

   Depends on (all optional, every call guarded):
     SC.saves       — identity, saved builds, taste profile
     SC.pricingApi  — live re-pricing of a saved build
     SC.renderManifest / SC.imageResolver — build thumbnails

   Public surface:
     SC.studio.boot()            — hydrate every Studio section present
     SC.studio.getIdentity()     — { email, firstName } or null
     SC.studio.upload(file, opts)— push a room photo or plan to the queue

   Nothing here throws. If a dependency is missing the section either
   renders its CMS defaults or stays hidden, per the theme convention.
   ============================================================ */

(function () {
  'use strict';

  if (typeof window === 'undefined') return;
  var SC = window.SC = window.SC || {};
  if (SC.studio) return; /* already booted */

  /* Note on credentials: only the NEW Worker routes (renders upload,
     direct samples, draft order) send the sc_id cookie. The original
     /api/saves calls in sc-saves.js deliberately do not, because their
     CORS still answers a wildcard; adding credentials there would break
     them in the browser. When those routes move behind requireAuth,
     change their CORS first, then the fetches. */

  /* ── Small helpers ─────────────────────────────────── */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function on(el, evt, fn) { if (el) el.addEventListener(evt, fn); }
  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }
  function text(el, str) { if (el) el.textContent = str == null ? '' : String(str); }

  /* Read a data-* config value from a section root, with a fallback. */
  function conf(root, key, fallback) {
    if (!root) return fallback;
    var v = root.getAttribute('data-' + key);
    return (v === null || v === '') ? fallback : v;
  }

  /* Substitute {name} in a heading pattern, falling back to the
     anonymous variant when we have no first name to use. */
  function personalise(pattern, anon, firstName) {
    if (!firstName) return anon || (pattern || '').replace(/\{name\},?\s*/gi, '');
    return (pattern || '').replace(/\{name\}/gi, firstName);
  }

  function money_(amount, currency) {
    var n = Number(amount);
    if (!isFinite(n) || n <= 0) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: currency || 'GBP', maximumFractionDigits: 0
      }).format(n);
    } catch (e) {
      return '\u00A3' + n.toLocaleString();
    }
  }

  /* Deliberately loose. The Worker validates properly and the only job
     here is to catch a typo before a file is uploaded against an address
     that cannot receive it. Refusing anything unusual but valid would be
     worse than accepting something odd. */
  function looksLikeEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim()) && String(v).length <= 254;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ── Identity ──────────────────────────────────────── */

  function getIdentity() {
    try {
      if (SC.saves && typeof SC.saves.getUser === 'function') {
        var u = SC.saves.getUser();
        if (u && u.email) return { email: u.email, firstName: u.firstName || '' };
      }
    } catch (e) {}
    return null;
  }

  function getBuilds() {
    try {
      if (SC.saves && typeof SC.saves.getBuilds === 'function') {
        return SC.saves.getBuilds() || [];
      }
    } catch (e) {}
    return [];
  }

  function getTaste() {
    try {
      if (SC.saves && typeof SC.saves.getTaste === 'function') return SC.saves.getTaste();
      var raw = localStorage.getItem('sc_taste');
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  /* ── Live pricing ──────────────────────────────────────
     sc-saves stores the price at save time (sc-saves.js:307), so a
     build saved before a price change shows a stale number. Where the
     pricing API is available we re-price on render and swap the value
     in; where it is not, the stored price stands rather than showing
     nothing. Failure is silent by design.                            */

  function repriceBuild(build, el) {
    if (!SC.pricingApi || typeof SC.pricingApi.getPrice !== 'function') return;
    if (!build || !build.config) return;
    el.classList.add('is-pending');
    SC.pricingApi.getPrice(build.config).then(function (res) {
      el.classList.remove('is-pending');
      if (!res || !res.price) return;
      var label = el.getAttribute('data-price-suffix') || '';
      el.innerHTML = escapeHtml(money_(res.price, res.currency)) +
        (label ? ' <small>' + escapeHtml(label) + '</small>' : '');
    }).catch(function () {
      el.classList.remove('is-pending');
    });
  }

  /* ── Build thumbnails ──────────────────────────────── */

  /* The picture of a saved build, resolved the way the rest of the site
     resolves one.

     This returned an empty string for every build, for three separate
     reasons, any one of which was enough on its own:

       - it read build.image, and a saved build carries imageUrl
         (sc-saves.js:362). Nothing has ever written build.image.
       - it called SC.imageResolver.forBuild, which does not exist. The
         resolver exposes _resolveFabricCode and config, not that.
       - it read data-sc-asset-base off document.body, and that attribute
         is on the script tag (theme.liquid:199), so the manifest branch
         produced a bare filename with no origin in front of it.

     The manifest is keyed on a composed SKU, never on build.sku, which is
     why that lookup found nothing even when a key existed. This is the
     same composition the gallery's build card uses, kept deliberately
     identical so the two cannot disagree about what a build looks like. */
  /* Cloudflare serves /cdn-cgi/image/ from the origin root only. cdnBase
     carries a path ("https://cdn.sophieconran.com/renders"), and pasting the
     resizing prefix on the end of it produced
     .../renders/cdn-cgi/image/.../file, which 404s: verified against the CDN,
     where the same file is 200 both unresized and with the prefix moved to the
     front. Split the origin from the path and rebuild it the way
     SC.renderRoom.thumbBase already does. */
  function sizedRenderUrl(base, file, w) {
    var m = String(base).replace(/\/$/, '').match(/^(https?:\/\/[^\/]+)(\/.*)?$/);
    var origin = m ? m[1] : String(base).replace(/\/$/, '');
    var path = (m && m[2]) ? m[2] : '';
    return origin + '/cdn-cgi/image/width=' + w + ',quality=80,format=auto'
      + path + '/' + encodeURIComponent(file);
  }

  var IMG_SIZE_CODES = {
    'armchair': '10', 'loveseat': '15', '2-seater': '20', '2.5-seater': '25',
    '3-seater': '30', '3.5-seater': '35', '4-seater': '40'
  };
  var IMG_STYLE_CODES = {
    coco: { c: 'K', v: '2' }, felix: { c: 'F', v: '1' }, isobel: { c: 'B', v: '1' },
    barton: { c: 'S', v: '2' }, henrietta: { c: 'F', v: '1' }, sophia: { c: 'F', v: '1' }
  };
  var IMG_RANGE_CODES = {
    coco: 'CO', felix: 'FE', isobel: 'IS', barton: 'BA', henrietta: 'HE', sophia: 'SO'
  };

  function buildImage(build) {
    if (!build) return '';
    if (build.imageUrl) return build.imageUrl;
    try {
      if (!build.range || !build.fabric || !SC.imageResolver || !SC.renderManifest) return '';
      var rk = String(build.range).toLowerCase();
      var rc = IMG_RANGE_CODES[rk];
      var st = IMG_STYLE_CODES[rk] || IMG_STYLE_CODES.coco;
      var sc = IMG_SIZE_CODES[build.size];
      var fc = SC.imageResolver._resolveFabricCode
        ? SC.imageResolver._resolveFabricCode(build.fabric) : null;
      if (!rc || !sc || !fc) return '';
      /* Six positions because a fabric can sit at any of them in the set,
         and the first that exists is the front view we want. */
      for (var pos = 1; pos <= 6; pos++) {
        var key = 'K' + rc + 'S' + sc + st.c + '-' + fc + st.v + pos + '-FR';
        if (SC.renderManifest[key]) {
          var cdn = (SC.imageResolver.config && SC.imageResolver.config.cdnBase)
            || 'https://cdn.sophieconran.com/renders';
          return sizedRenderUrl(cdn, SC.renderManifest[key], 480);
        }
      }
    } catch (e) {}
    return '';
  }

  /* ── Saved builds section ──────────────────────────── */

  function hydrateBuilds(root) {
    var grid = $('[data-studio-builds-grid]', root);
    var empty = $('[data-studio-builds-empty]', root);
    if (!grid) return;

    var builds = getBuilds();
    var details = $('[data-studio-builds-details]', root);
    var countEl = $('[data-studio-builds-count]', root);

    if (!builds.length) {
      hide(grid); show(empty);
      if (details) hide(details);
      return;
    }
    hide(empty); show(grid);
    if (details) show(details);

    /* One build is already on show in the hero, so repeating it here
       adds nothing and the section starts closed. Two or more and it
       opens, because comparing them is the reason the section exists. */
    if (details) {
      details.open = builds.length > 1;
      var tpl = builds.length === 1
        ? conf(root, 'summary-label', 'Your saved sofa')
        : conf(root, 'summary-label-plural', 'Your {n} saved sofas');
      if (countEl) text(countEl, tpl.replace(/\{n\}/g, String(builds.length)));
    }

    var priceSuffix = conf(root, 'price-suffix', 'as configured');
    var openLabel = conf(root, 'open-label', 'Open build');
    var sampleLabel = conf(root, 'sample-label', 'Order this fabric sample');
    var editLabel = conf(root, 'edit-label', 'Edit');
    var tagLabel = conf(root, 'tag-label', 'Your build');

    grid.innerHTML = builds.map(function (b) {
      var img = buildImage(b);
      var name = b.rangeName || b.model || b.name || 'Your sofa';
      var size = b.sizeName || b.size || '';
      var fabric = b.fabricName || b.fabric || '';
      var stored = money_(b.price, b.currency);

      return '' +
        '<article class="sc-studio-build" data-build-id="' + escapeHtml(b.id || '') + '">' +
          '<div class="sc-studio-build-img">' +
            '<span class="sc-studio-build-tag">' + escapeHtml(tagLabel) + '</span>' +
            (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(name + ' in ' + fabric) + '" loading="lazy">' : '') +
          '</div>' +
          '<div class="sc-studio-build-body">' +
            '<h3>' + escapeHtml(name) + (size ? ' <span>' + escapeHtml(size) + '</span>' : '') + '</h3>' +
            (fabric ? '<div class="sc-studio-build-sub">' + escapeHtml(fabric) + '</div>' : '') +
            '<div class="sc-studio-build-price-row">' +
              '<span class="sc-studio-build-price" data-studio-price data-price-suffix="' + escapeHtml(priceSuffix) + '">' +
                (stored ? escapeHtml(stored) + ' <small>' + escapeHtml(priceSuffix) + '</small>' : '') +
              '</span>' +
              '<button type="button" class="sc-btn sc-btn-secondary sc-btn-sm" data-studio-open>' + escapeHtml(openLabel) + '</button>' +
            '</div>' +
            '<div class="sc-studio-build-links">' +
              '<button type="button" data-studio-sample>' + escapeHtml(sampleLabel) + '</button>' +
              '<button type="button" data-studio-edit>' + escapeHtml(editLabel) + '</button>' +
            '</div>' +
          '</div>' +
        '</article>';
    }).join('');

    /* Wire each card back to its build object */
    $$('.sc-studio-build', grid).forEach(function (card, i) {
      var b = builds[i];
      var priceEl = $('[data-studio-price]', card);
      if (priceEl) repriceBuild(b, priceEl);

      on($('[data-studio-open]', card), 'click', function () {
        var url = b.pageUrl || (SC.saves && SC.saves.buildShareUrl ? SC.saves.buildShareUrl(b) : '');
        if (url) window.location.href = url;
      });
      on($('[data-studio-edit]', card), 'click', function () {
        if (b.pageUrl) window.location.href = b.pageUrl;
      });
      on($('[data-studio-sample]', card), 'click', function () {
        var base = conf(root, 'sample-url', '/pages/fabric-samples');
        var fab = b.fabricName || b.fabric || '';
        window.location.href = base + (fab ? '?fabric=' + encodeURIComponent(fab) : '');
      });
    });
  }

  /* ── Hero ──────────────────────────────────────────── */

  /* ── The render request flow ───────────────────────────

     Kind first, then which pieces, then the file. The piece choice is not
     a step: it sits above the panes from the moment a kind is picked, so
     it can be changed at any point right up to sending.

     Selection is held by build id, never by index. saveBuild unshifts and
     caps at 20, and refreshFromServer replaces the array wholesale a few
     hundred milliseconds after boot, so an index means a different sofa
     than the one that was tapped. */
  var MAX_PLACED = 3;
  var placedIds = null;   /* null means "not chosen yet", not "none chosen" */

  function sizeWords(b) {
    var v = b.sizeName || b.size || '';
    return String(v).replace(/-/g, ' ');
  }
  function pieceName(b) {
    var r = b.rangeName || b.range || 'sofa';
    var sz = sizeWords(b);
    return sz ? r + ' ' + sz : r;
  }
  function fabricWords(b) {
    var f = b.fabricName || b.fabric || '';
    /* Never read their own material back to them as a fabric name. */
    if (!f || /own material|own fabric|bespoke/i.test(f)) return '';
    return String(f);
  }
  function pieceLabel(b) {
    var f = fabricWords(b);
    return f ? pieceName(b) + ' in ' + f : pieceName(b);
  }
  function joinWords(a) {
    if (!a.length) return '';
    if (a.length === 1) return a[0];
    return a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1];
  }

  /* Resolves ids against the builds that exist right now. Never returns a
     hole: an id that has stopped resolving, which a server sync can cause,
     falls back to the newest build rather than to [undefined], which is
     what upload() would otherwise try to read a range off. */
  function placedBuilds() {
    var all = getBuilds();
    if (!all.length) return [];
    if (!placedIds) return [all[0]];
    var out = [];
    all.forEach(function (b) {
      if (b && b.id && placedIds.indexOf(b.id) !== -1) out.push(b);
    });
    return out.length ? out : [all[0]];
  }

  /* The selection as data. Tildes and pipes rather than JSON: an encoded
     object would spend most of a query string on punctuation, and these
     three fields are all anything downstream has asked for. */
  function placedSpec() {
    return placedBuilds().map(function (b) {
      return [b.range || '', b.size || '', (b.fabricName || b.fabric || '')]
        .map(function (v) { return String(v).replace(/[~|]/g, ' ').trim(); })
        .join('~');
    }).join('|').slice(0, 400);
  }

  function placedLabel() {
    var bs = placedBuilds();
    if (!bs.length) return '';
    return joinWords(bs.map(pieceLabel)).slice(0, 200);
  }

  function placedPhrase() {
    var bs = placedBuilds();
    if (!bs.length) return 'your sofa';
    return joinWords(bs.map(function (b) { return 'your ' + pieceName(b); }));
  }

  function renderPieces(root) {
    var wrap = $('[data-studio-pieces]', root);
    var noteWrap = $('[data-studio-note-wrap]', root);
    var chips = $('[data-studio-pieces-chips]', root);
    var q = $('[data-studio-pieces-q]', root);
    if (!wrap || !chips) return;

    var builds = getBuilds();

    /* The note is always offered, because a bespoke request is a sentence
       and not a set of chips: a customer wanting a corner unit in a bay
       window, or their own material, had nowhere to say so.

       With nothing configured it is the only brief there is, so the label
       asks the question outright and doSend requires an answer. With
       builds saved it is an optional extra line. */
    show(noteWrap);
    var label = $('[data-studio-note-label]', root);
    if (label) {
      text(label, builds.length
        ? conf(root, 'ask-note-extra', 'Anything else we should know?')
        : conf(root, 'ask-note', 'What would you like to see in the room?'));
    }
    if (!builds.length) { hide(wrap); return; }
    show(wrap);

    if (!placedIds) placedIds = (builds[0] && builds[0].id) ? [builds[0].id] : [];

    if (builds.length === 1) {
      /* One build is not a choice, so it is a sentence rather than a
         control nobody can act on. */
      text(q, conf(root, 'ask-one', 'We will place {piece}.')
        .replace('{piece}', 'your ' + pieceName(builds[0])));
      chips.innerHTML = '';
      hide(chips);
      return;
    }

    show(chips);
    text(q, conf(root, 'ask-many', 'Which pieces should we place?'));
    /* sc-studio-pchip, never sc-studio-chip: that name already belongs to
       the card floating over the hero's stage image, absolutely positioned
       at bottom left. Sharing it sent every one of these buttons to the
       top corner of the page and turned the stage card into a pill.

       A row per piece rather than a bare pill, with the same render the
       build card uses, because choosing between sofas by photograph is a
       different act from choosing between two lines of text. */
    chips.innerHTML = builds.map(function (b) {
      var isOn = placedIds.indexOf(b.id) !== -1;
      var img = buildImage(b);
      var f = fabricWords(b);
      return '<button type="button" class="sc-studio-pchip' + (isOn ? ' is-on' : '') + '"'
        + ' data-studio-piece="' + escapeHtml(b.id) + '"'
        + ' aria-pressed="' + (isOn ? 'true' : 'false') + '">'
        + '<span class="sc-studio-pchip-img">'
        +   (img ? '<img src="' + escapeHtml(img) + '" alt="" loading="lazy">' : '')
        + '</span>'
        + '<span class="sc-studio-pchip-text">'
        +   '<strong>' + escapeHtml(pieceName(b)) + '</strong>'
        +   (f ? '<span>' + escapeHtml(f) + '</span>' : '')
        + '</span>'
        + '<span class="sc-studio-pchip-tick" aria-hidden="true"></span>'
        + '</button>';
    }).join('');
  }

  /* The hero's result caption, repainted so tapping a chip visibly renames
     the sofa in the picture beside it. Without this the choice happens in
     one column and is invisible in the other. */
  function reflectPieces(root) {
    var cap = $('[data-studio-eq-caption]', root) || $('[data-studio-build-label]', root);
    if (cap) text(cap, placedLabel());
  }

  function wireAsk(root) {
    var ask = $('[data-studio-ask]', root);
    if (!ask) return;

    var kindWrap = $('[data-studio-kind-choice]', ask);
    var back = $('[data-studio-back]', ask);
    var msg = $('[data-studio-pieces-msg]', ask);

    function showStep(step) {
      /* There is no 'choose' any more.

         The page used to open on the question with both boxes hidden, so
         the clearest thing on it, a dashed frame with a plus in it, was
         behind a decision. It opens on the room box now and asks which
         kind underneath, as a toggle. Anything that is not an explicit
         'plan' resolves to 'room': that covers the empty attribute, and a
         stored 'choose' handed back by the sessionStorage resume below
         from a session that started before this change. Without that
         normalisation an old value would hide both boxes and leave the
         customer facing an upload page with nothing to upload into. */
      if (step !== 'plan') step = 'room';
      ask.setAttribute('data-step', step);

      /* Both always on show now: the toggle is how you get back, so the
         separate back link went with the step it undid. */
      show(kindWrap);
      hide(back);
      renderPieces(ask);

      /* Only the chosen kind's dropzone is on show, so the static
         data-kind on each zone stays the only thing that decides what the
         Worker is told. A bug here cannot queue a plan as a room photo. */
      $$('[data-studio-drop]', root).forEach(function (z) {
        (z.getAttribute('data-kind') === step ? show : hide)(z);
      });

      /* Which button is lit is state, and it lives here rather than in the
         markup. It used to ship as an is-primary class on the room button,
         which said "recommended" permanently and went on saying it after
         the customer chose the floor plan. */
      $$('[data-studio-pick-kind]', ask).forEach(function (b) {
        var isOn = b.getAttribute('data-studio-pick-kind') === step;
        b.classList.toggle('is-on', isOn);
        b.setAttribute('aria-pressed', isOn ? 'true' : 'false');
      });
    }

    $$('[data-studio-pick-kind]', ask).forEach(function (btn) {
      on(btn, 'click', function () {
        var kind = btn.getAttribute('data-studio-pick-kind');
        showStep(kind);
        reflectPieces(root);
        /* No longer opens the file dialog on the way past.

           That was right when this was a question standing in front of a
           hidden box: choosing an answer and picking the file were one
           gesture. With the box permanently open above the toggle it
           would be a trick, a button labelled "Floor plan" that takes over
           the screen with a file browser. The box is the tap that opens
           the picker, and it is the thing that looks like it would. */
      });
    });

    on(back, 'click', function () { showStep('choose'); });

    /* Delegated: the chips are rebuilt whenever the builds change. */
    on(ask, 'click', function (e) {
      var chip = e.target && e.target.closest && e.target.closest('[data-studio-piece]');
      if (!chip || !ask.contains(chip)) return;
      var id = chip.getAttribute('data-studio-piece');
      if (!placedIds) placedIds = [];
      var i = placedIds.indexOf(id);
      if (i !== -1) {
        if (placedIds.length === 1) {
          text(msg, conf(root, 'ask-min-msg', 'Keep at least one piece.'));
          return;
        }
        placedIds.splice(i, 1);
      } else {
        if (placedIds.length >= MAX_PLACED) {
          text(msg, conf(root, 'ask-max-msg', 'Up to {n} pieces in one image.')
            .replace('{n}', String(MAX_PLACED)));
          return;
        }
        placedIds.push(id);
      }
      text(msg, '');
      renderPieces(ask);
      reflectPieces(root);
    });

    /* Carry the choice through the quiz and back, so "configure another"
       returns to a page that still remembers what was already picked. */
    var more = $('[data-studio-add-piece]', ask);
    on(more, 'click', function () {
      try {
        sessionStorage.setItem('sc_studio_placing', JSON.stringify({
          ids: placedIds || [], at: Date.now(), step: ask.getAttribute('data-step')
        }));
      } catch (e) {}
    });

    /* Coming back: re-tick what was chosen, and tick anything saved while
       they were away, which is the piece they just went to configure.
       Without the second half, "configure another" ends with only the new
       piece selected, which defeats the journey. */
    var resumed = '';
    try {
      var raw = sessionStorage.getItem('sc_studio_placing');
      if (raw) {
        var prev = JSON.parse(raw);
        sessionStorage.removeItem('sc_studio_placing');
        var now = getBuilds();
        var keep = (prev.ids || []).filter(function (id) {
          return now.some(function (b) { return b.id === id; });
        });
        now.forEach(function (b) {
          if (b && b.id && b.savedAt && Date.parse(b.savedAt) >= (prev.at || 0)
              && keep.indexOf(b.id) === -1 && keep.length < MAX_PLACED) keep.push(b.id);
        });
        if (keep.length) placedIds = keep;
        if (prev.step && prev.step !== 'choose') resumed = prev.step;
      }
    } catch (e) {}

    showStep(resumed || ask.getAttribute('data-step') || 'choose');
  }

  /* The locked teaser, once they have already built something.

     The gate is about what we offer, not about what we say. A customer who
     has saved a sofa but not yet reached the tier renders are offered at
     was being told "save a sofa you love and we will place it", with their
     saved sofa on the page a few hundred pixels above. Same gate, words
     that match what they have actually done. */
  function reflectLocked(root) {
    var box = $('.sc-studio-eq-locked', root);
    if (!box || !getBuilds().length) return;
    var title = conf(root, 'locked-title-built', '');
    var body = conf(root, 'locked-body-built', '');
    var cta = conf(root, 'locked-cta-built', '');
    if (title) text($('[data-studio-locked-title]', box), title);
    if (body) text($('[data-studio-locked-body]', box), body);
    /* The link is left alone: it already points at the quiz, which is
       where "add another piece" goes too. */
    if (cta) text($('[data-studio-locked-cta]', box), cta);
  }

  function hydrateHero(root) {
    var ident = getIdentity();
    var first = ident && ident.firstName ? ident.firstName : '';

    var h1 = $('[data-studio-heading]', root);
    if (h1) {
      text(h1, personalise(
        conf(root, 'heading-pattern', '{name}, see your sofa in your own sitting room.'),
        conf(root, 'heading-anon', 'See your sofa in your own sitting room.'),
        first
      ));
    }

    /* The chip describes the customer's most recent build where we have
       one, and otherwise keeps whatever the CMS defaults say. */
    var builds = getBuilds();
    if (builds.length) {
      var b = builds[0];
      var chipName = $('[data-studio-chip-name]', root);
      var chipSub = $('[data-studio-chip-sub]', root);
      var chipPrice = $('[data-studio-chip-price]', root);
      var name = (b.rangeName || b.model || '') + (b.sizeName ? ' ' + b.sizeName : '');
      if (chipName && name.trim()) text(chipName, name.trim());
      if (chipSub && (b.fabricName || b.fabric)) {
        text(chipSub, (b.fabricName || b.fabric) + ' \u00B7 ' + conf(root, 'chip-sub-suffix', 'your configuration'));
      }
      if (chipPrice) {
        var stored = money_(b.price, b.currency);
        var suffix = conf(root, 'price-suffix', 'as configured');
        if (stored) chipPrice.innerHTML = escapeHtml(stored) + ' <small>' + escapeHtml(suffix) + '</small>';
        repriceBuild(b, chipPrice);
      }
      var lede = $('[data-studio-lede]', root);
      if (lede && b.rangeName) {
        lede.textContent = lede.textContent.replace(/\{range\}/gi, b.rangeName);
      }
    }
    /* Strip any unfilled placeholder so it never reaches the customer */
    $$('[data-studio-lede]', root).forEach(function (el) {
      el.textContent = el.textContent.replace(/\{range\}/gi, conf(root, 'range-fallback', 'sofa'));
    });

    /* Example room tabs */
    var stage = $('[data-studio-stage]', root);
    var tag = $('[data-studio-stage-tag]', root);

    /* Which tab is lit is decided in Liquid by forloop.first, but the
       picture beside it falls back to the first room block only when the
       Stage image setting is blank. Set that setting and the two disagree
       silently: tab one is highlighted while the picture shows something
       else. That was invisible while the tabs were only a picture
       switcher. It is not any more, because the lit tab is now sent with
       the upload as roomType, so a disagreement would tell the studio the
       wrong kind of room. Settle it against the picture, which is the
       thing the customer can actually see. */
    if (stage) {
      var showing = (stage.getAttribute('src') || '').split('?')[0];
      var tabs = $$('[data-studio-tab]', root);
      var match = null;
      tabs.forEach(function (t) {
        if (!match && (t.getAttribute('data-full') || '').split('?')[0] === showing) match = t;
      });
      if (match) {
        tabs.forEach(function (t) { t.classList.remove('is-active'); });
        match.classList.add('is-active');
        text(tag, match.getAttribute('data-label') || '');
      }
    }

    $$('[data-studio-tab]', root).forEach(function (tab) {
      on(tab, 'click', function () {
        $$('[data-studio-tab]', root).forEach(function (t) { t.classList.remove('is-active'); });
        tab.classList.add('is-active');
        var src = tab.getAttribute('data-full') || '';
        var label = tab.getAttribute('data-label') || '';
        /* A photograph is cropped to fill the frame, a plan is fitted whole:
           cover on a floor plan crops exactly the edges the measurements are
           written along, which makes the one thing it is for unreadable. */
        var kind = tab.getAttribute('data-kind') || 'room';
        if (!stage || !src) return;
        stage.style.opacity = '0';
        setTimeout(function () {
          stage.src = src;
          stage.setAttribute('data-kind', kind);
          stage.style.opacity = '1';
          text(tag, label);
        }, 180);
      });
    });

    /* Share */
    var shareBtn = $('[data-studio-share]', root);
    var shareMsg = $('[data-studio-share-msg]', root);
    on(shareBtn, 'click', function () {
      var url = window.location.href;
      var done = conf(root, 'share-done', 'Link copied. This page is yours to share.');
      function ok() {
        text(shareMsg, done);
        setTimeout(function () { text(shareMsg, ''); }, 3500);
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(ok).catch(ok);
      } else { ok(); }
    });
  }

  /* ── Uploads ───────────────────────────────────────────
     Contract with the Worker (endpoint set per section, defaults to
     the Studio theme setting):

       POST {endpoint}   multipart/form-data
         file        the image or plan
         kind        "room" | "plan"
         email       identity from SC.saves, may be empty
         firstName   may be empty
         buildId     the build this render is for, may be empty
         buildLabel  human readable, for the editing team's queue
         pageUrl     where the upload came from

       200 { ok: true, id, queuePosition, etaLabel }
       4xx { ok: false, error: "message the customer can read" }

     etaLabel lets the Worker override the SLA copy when the queue is
     long, so backpressure is applied server-side without a deploy.   */

  var DEFAULT_MAX_MB = 50;

  /* The hero carries two dropzones, so a fixed id would produce two
     elements sharing one, and a label that points at the wrong box. */
  var zoneSeq = 0;

  /* The file goes up as the raw request body on a PUT, with everything
     else in query parameters, rather than as multipart form data.

     Not a workaround for a plan limit: it is simply the better shape.
     Multipart makes the Worker decode the whole body in memory before it
     can do anything, which costs CPU proportional to file size and holds
     the file in memory twice. A raw body streams straight through to R2,
     so cost is flat regardless of size and memory stays bounded. It is
     also less code. See renders.js in the Worker for the other half. */
  function upload(file, opts) {
    opts = opts || {};
    var endpoint = opts.endpoint;
    if (!endpoint) return Promise.reject(new Error('no endpoint'));

    var ident = getIdentity() || {};

    /* What the customer chose, not whatever they happened to save last.

       This took builds[0] unconditionally, so somebody with three saved
       sofas had the newest one sent with no say in it and no sign that
       had happened. placedBuilds() returns their selection, defaulting to
       the newest when no chip has been touched, so a customer who ignores
       the chooser gets exactly what this sent before.

       A sentence rather than the old middot list, because a person reads
       it in Slack and it is quoted back to the customer in their delivery
       email. Sliced there so the Worker's own silent 200 character cut
       cannot bite mid-word into something they will read. */
    var label = placedLabel();

    /* The note travels in its own parameter rather than being folded into
       buildLabel: the label names the pieces and is quoted back to the
       customer in their delivery email, while the note is an instruction
       for the studio, and running the two together would put a customer's
       aside inside a sentence describing their sofa. */
    var noteEl = document.querySelector('[data-studio-note]');
    var note = noteEl && noteEl.value ? noteEl.value.trim().slice(0, 300) : '';

    /* Which example room the customer picked as closest to their own.

       The four thumbnails sit at the foot of the block and change the
       example picture above them. On a phone that picture has scrolled
       away by the time they are read, so as a picture switcher alone they
       were a control with no visible effect. Sent with the photograph they
       are worth answering: the studio starts from the right kind of room
       rather than inferring it from the image.

       Read at send time rather than held in a variable, so it is whatever
       is lit at the moment the file goes, and absent rather than guessed
       when no tab is active. */
    var activeTab = document.querySelector('[data-studio-tab].is-active');
    var roomType = activeTab ? (activeTab.getAttribute('data-label') || '') : '';

    var qs =
      '?email=' + encodeURIComponent(ident.email || '') +
      '&kind=' + encodeURIComponent(opts.kind || 'room') +
      '&firstName=' + encodeURIComponent(ident.firstName || '') +
      '&buildLabel=' + encodeURIComponent(label) +
      (note ? '&note=' + encodeURIComponent(note) : '') +
      /* The same pieces again, but as data rather than a sentence, so the
         automation can read range, size and fabric separately. Compact
         because this rides on a query string: range~size~fabric, pieces
         separated by a pipe. */
      (placedSpec() ? '&builds=' + encodeURIComponent(placedSpec()) : '') +
      (roomType ? '&roomType=' + encodeURIComponent(roomType) : '') +
      '&name=' + encodeURIComponent(file.name || 'upload') +
      '&pageUrl=' + encodeURIComponent(window.location.href);

    return fetch(endpoint + qs, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file
    }).then(function (r) {
      return r.json().then(function (d) {
        if (!r.ok || !d || d.ok === false) throw new Error((d && d.error) || 'Upload failed');
        return d;
      });
    });
  }

  /* A dropzone moves through five states: idle, picked, sending, done,
     error. Picking a file (by click or by drop) no longer sends it —
     it shows the filename and a Send button, so there is a deliberate
     confirmation step rather than a silent auto-upload. Every state
     renders INSIDE the zone itself, because the previous version wrote
     its only feedback to a status line elsewhere on the page, which is
     exactly why picking a file looked like it did nothing: something
     did happen, just not where anyone was looking.

     The zone element itself is never replaced, only its contents, and
     every listener is bound once at the zone level using delegation
     (checking e.target inside the handler) rather than rebound after
     each render. The <input> is recreated whenever idle markup is
     restored, so binding to it directly would go stale on the second
     use; listening for 'change' at the zone and reading e.target avoids
     that without needing to rebind anything. */
  function wireDropzone(zone, root) {
    if (!zone) return;
    var kind = zone.getAttribute('data-kind') || 'room';
    var endpoint = conf(root, 'upload-endpoint', '');
    var maxMb = Number(conf(root, 'max-mb', DEFAULT_MAX_MB)) || DEFAULT_MAX_MB;
    var statusId = zone.getAttribute('data-status');
    var status = statusId ? document.getElementById(statusId) : null;

    var pendingMsg = zone.getAttribute('data-pending') || 'Sending your photo\u2026';
    var doneMsg = zone.getAttribute('data-done') || 'Photo received.';
    /* The same copy, {sla} left as a literal token rather than already
       resolved to the theme's static setting. Falls back to doneMsg
       itself if a section predates this attribute, so an older template
       degrades to the previous (correct, just non-dynamic) behaviour
       rather than breaking. */
    var doneTemplate = zone.getAttribute('data-done-template') || doneMsg;
    var sendLabel = zone.getAttribute('data-send-label') || 'Send this photo';
    var changeLabel = zone.getAttribute('data-change-label') || 'Choose a different photo';
    var retryLabel = zone.getAttribute('data-retry-label') || 'Try again';
    var errorGeneric = 'Something went wrong. Please try again, or send it to us and we will sort it.';

    /* Only ever seen by somebody who has not given us an address yet,
       which after launch is most people arriving on this page cold. The
       defaults stand on their own so the field works on a theme where
       these settings were never filled in. */
    var emailLabel = zone.getAttribute('data-email-label')
      || 'Where shall we send it?';
    var emailPlaceholder = zone.getAttribute('data-email-placeholder')
      || 'your@email.com';
    var emailError = zone.getAttribute('data-email-error')
      || 'That does not look like an email address. We need one to send your render to.';

    /* Unique per zone, so the label points at the right box on a page
       carrying more than one. */
    zone.dataset.emailId = String(++zoneSeq);

    var idleHTML = zone.innerHTML;
    var selectedFile = null;

    /* role="status" on the zone means every state change is announced
       to a screen reader as it happens, on top of whatever the external
       status element (if any) also says. */
    zone.setAttribute('role', 'status');

    function setStatus(msg, isError) {
      if (!status) return;
      status.textContent = msg || '';
      status.classList.toggle('is-error', !!isError);
    }

    function render(state, msg) {
      var was = zone.dataset.dropState || 'idle';
      zone.dataset.dropState = state;
      zone.classList.toggle('is-error-state', state === 'error');

      /* Keep the box in view when it grows.

         Picking a file replaces the contents of this zone with a preview,
         possibly an email field, and the Send button. The box now sits at
         the top of the page rather than below the question, so on a phone
         that growth can push Send past the bottom of the screen, leaving a
         customer who has just chosen a photograph looking at a page that
         appears to have done nothing. Centre it instead, once, on the
         transition into picked rather than on every render, so it cannot
         fight someone scrolling while they read. */
      if (state === 'picked' && was !== 'picked' && zone.scrollIntoView) {
        try { zone.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
      }
      if (state === 'idle') {
        zone.innerHTML = idleHTML;
      } else if (state === 'picked') {
        /* Asked for here, at the moment it is obviously needed, rather
           than up front as a toll gate. Somebody who has just chosen a
           photograph of their sitting room has told us what they want;
           asking for an address to send it to is the next sentence in
           that conversation, not a form.

           Only when we do not already have one. Anybody arriving from
           the quiz, or who has saved a build, never sees this. */
        var needsEmail = !(getIdentity() || {}).email;
        var fieldId = 'sc-studio-email-' + (zone.dataset.emailId || '');
        zone.innerHTML =
          '<div class="sc-studio-drop-picked">' +
            '<span class="sc-studio-drop-filename">' + escapeHtml(selectedFile.name) + '</span>' +
          '</div>' +
          (needsEmail
            ? '<div class="sc-studio-drop-email">' +
                '<label for="' + fieldId + '">' + escapeHtml(emailLabel) + '</label>' +
                '<input type="email" id="' + fieldId + '" data-studio-email ' +
                  'inputmode="email" autocomplete="email" spellcheck="false" ' +
                  'placeholder="' + escapeHtml(emailPlaceholder) + '">' +
              '</div>'
            : '') +
          '<div class="sc-studio-drop-actions">' +
            '<button type="button" class="sc-btn sc-btn-primary sc-btn-sm" data-action="send">' + escapeHtml(sendLabel) + '</button>' +
            '<button type="button" class="sc-studio-drop-link" data-action="change">' + escapeHtml(changeLabel) + '</button>' +
          '</div>';
        if (needsEmail) {
          var f = $('[data-studio-email]', zone);
          /* Enter should send, because a lone text field with a button
             beside it behaves like a form to everybody who is not
             reading the markup. */
          on(f, 'keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); send(); }
          });
        }
      } else if (state === 'sending') {
        zone.innerHTML = '<p class="sc-studio-drop-msg">' + escapeHtml(pendingMsg) + '</p>';
      } else if (state === 'done') {
        zone.innerHTML =
          '<div class="sc-studio-drop-tick" aria-hidden="true">&#10003;</div>' +
          '<p class="sc-studio-drop-msg">' + escapeHtml(msg) + '</p>';
      } else if (state === 'error') {
        zone.innerHTML =
          '<p class="sc-studio-drop-msg sc-studio-drop-error">' + escapeHtml(msg) + '</p>' +
          '<button type="button" class="sc-btn sc-btn-secondary sc-btn-sm" data-action="retry">' + escapeHtml(retryLabel) + '</button>';
      }
    }

    function pick(file) {
      if (!file) return;
      if (file.size > maxMb * 1024 * 1024) {
        selectedFile = null;
        render('error', 'That photo is larger than ' + maxMb + 'MB, which is more than this can take. '
          + 'A slightly smaller copy will go through, and so will any of the other ways to reach us.');
        return;
      }
      selectedFile = file;
      render('picked');
    }

    /* Stores the address and merges anything saved as a guest, the same
       call the sign-in modal makes. It writes to local storage before it
       touches the network, so the upload has what it needs whether or
       not the sync ever comes back. The timeout is there so a slow
       server cannot leave somebody staring at a stalled upload. */
    function adopt(email, then) {
      var went = false;
      var go = function () { if (!went) { went = true; then(); } };
      try {
        if (window.SC && SC.saves && typeof SC.saves.adoptAndSync === 'function') {
          setTimeout(go, 2500);
          SC.saves.adoptAndSync(email, '', go);
          return;
        }
        if (window.SC && SC.saves && typeof SC.saves.signIn === 'function') {
          SC.saves.signIn(email, '');
        }
      } catch (e) {}
      go();
    }

    function send() {
      if (!selectedFile) return;
      var file = selectedFile;

      /* Validate before rendering anything: 'sending' replaces the
         zone's contents, and with them the field being read. Getting
         this order wrong loses what they typed. */
      if (!(getIdentity() || {}).email) {
        var field = $('[data-studio-email]', zone);
        var typed = field ? String(field.value || '').trim() : '';
        if (!looksLikeEmail(typed)) {
          if (field) {
            field.classList.add('is-error');
            field.focus();
          }
          setStatus(emailError, true);
          return;   /* stays in picked, so the file is still chosen */
        }
        render('sending');
        setStatus(pendingMsg, false);
        adopt(typed, function () { doSend(file); });
        return;
      }

      render('sending');
      setStatus(pendingMsg, false);
      doSend(file);
    }

    function doSend(file) {

      /* A brief is required, in one form or the other.

         With nothing configured and nothing typed, the studio receives a
         photograph of a room and no idea what to put in it, which is a
         request nobody can action and which comes back to the customer as
         silence. Anyone with a saved build has already answered this by
         having one, so this only ever stops the case it exists for. */
      var noteEl = document.querySelector('[data-studio-note]');
      /* Only when the field is actually on the page. Requiring an answer
         to a question the customer has not been asked is a dead end, and
         the field is absent on any template that does not render the
         chooser. */
      if (noteEl && !getBuilds().length) {
        var noteMsg = document.querySelector('[data-studio-note-msg]');
        var note = noteEl.value ? noteEl.value.trim() : '';
        if (!note) {
          if (noteMsg) {
            noteMsg.textContent = conf(root, 'ask-note-msg',
              'Tell us what you are after and we can get started.');
          }
          if (noteEl.focus) { try { noteEl.focus(); } catch (e) {} }
          return;
        }
        if (noteMsg) noteMsg.textContent = '';
      }

      if (!endpoint) {
        /* No endpoint configured, so nothing has been sent anywhere.

           This used to render the normal success message, which made a
           misconfigured page indistinguishable from a working one: the
           customer, and anyone testing, saw "photo received" when the
           file had gone nowhere at all. Say what actually happened
           instead. The wording is deliberately plain rather than
           reassuring, because the only person who should ever see it is
           whoever is setting the page up.

           recordEvent is NOT called here either: marking someone as
           having uploaded a room photo, when no photo exists, would
           promote them up the intent ladder on the strength of a file
           that was never sent. */
        var notSent = 'Not sent: no upload endpoint is configured. '
          + 'Set it under Theme settings \u203a Sofa Studio \u203a Upload endpoint.';
        render('error', notSent);
        setStatus(notSent, true);
        if (window.console && console.warn) {
          console.warn('[sc-studio] Upload skipped: the upload endpoint setting is empty. '
            + 'Nothing was sent to Cloudflare.');
        }
        return;
      }

      upload(file, { kind: kind, endpoint: endpoint }).then(function (d) {
        /* Re-run the SAME {sla} substitution the theme already did at
           render time, but with the Worker's live queue-depth figure in
           place of the static setting. Only reached when etaLabel is
           actually present, i.e. only when the promise needs lengthening;
           the common case uses the pre-baked doneMsg unchanged. */
        var msg = d.etaLabel ? doneTemplate.replace(/\{sla\}/gi, d.etaLabel) : doneMsg;
        render('done', msg);
        setStatus(msg, false);
        if (kind === 'room') recordEvent('roomUpload');
      }).catch(function (err) {
        var msg = (err && err.message) || errorGeneric;
        render('error', msg);
        setStatus(msg, true);
      });
    }

    /* One click listener on the zone, for every state. Idle opens the
       file picker; picked/error route through data-action, which also
       covers Change and Try again without a separate binding for each. */
    on(zone, 'click', function (e) {
      var actionEl = e.target.closest && e.target.closest('[data-action]');
      if (actionEl) {
        e.stopPropagation();
        var action = actionEl.getAttribute('data-action');
        if (action === 'send') send();
        else render('idle');
        return;
      }
      if ((zone.dataset.dropState || 'idle') === 'idle') {
        var input = $('input[type="file"]', zone);
        if (input) input.click();
      }
    });
    on(zone, 'keydown', function (e) {
      if ((zone.dataset.dropState || 'idle') !== 'idle') return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        var input = $('input[type="file"]', zone);
        if (input) input.click();
      }
    });
    /* Delegated rather than bound to the input directly: idle markup,
       and with it a fresh <input>, is re-rendered every time someone
       picks Change or Try again, so a direct binding would only ever
       fire once. */
    on(zone, 'change', function (e) {
      if (e.target && e.target.matches && e.target.matches('input[type="file"]')) {
        pick(e.target.files && e.target.files[0]);
      }
    });

    ['dragenter', 'dragover'].forEach(function (evt) {
      on(zone, evt, function (e) {
        e.preventDefault();
        if ((zone.dataset.dropState || 'idle') === 'idle') zone.classList.add('is-over');
      });
    });
    ['dragleave', 'drop'].forEach(function (evt) {
      on(zone, evt, function (e) { e.preventDefault(); zone.classList.remove('is-over'); });
    });
    on(zone, 'drop', function (e) {
      if ((zone.dataset.dropState || 'idle') !== 'idle') return;
      var dt = e.dataTransfer;
      if (dt && dt.files && dt.files.length) pick(dt.files[0]);
    });

    zone.dataset.dropState = 'idle';
  }

  /* Renders that have come back. The read is guarded server-side, so an
     unverified visitor gets requiresVerification and this quietly shows
     nothing rather than prompting: someone who has just landed should
     not meet a code request for a panel they may not even have. The
     prompt belongs on a deliberate action, not on page load. */
  function loadReturned(root) {
    var wrap = $('[data-studio-returned]', root);
    var grid = $('[data-studio-returned-grid]', root);
    var awaiting = $('[data-studio-awaiting]', root);
    var verify = $('[data-studio-verify]', root);
    var verifyBtn = $('[data-studio-verify-btn]', root);
    var verifyMsg = $('[data-studio-verify-msg]', root);
    if (!wrap || !grid) return;

    /* Nothing sent yet, or nobody identified: point them back up the page
       rather than leaving an empty band with no explanation. */
    function nothingYet() { if (awaiting) show(awaiting); }

    /* Off by default, and this is the switch that decides whether the
       Studio needs a sign-in at all.

       Reading renders back means handing over photographs of the inside
       of someone's home, which cannot be done on an unverified email
       address. Delivering them by email instead makes the customer's
       inbox the access control: the link is signed, unguessable and
       expiring, so possession of the email is the proof.

       Everything else on this page — builds, taste, gallery picks, the
       deposit split on their own build — is already readable by email
       address on the live site today. So with this off, the Studio adds
       no new exposure and needs no verification, no transactional
       sender and no identity deploy. */
    if (conf(root, 'show-renders', 'false') !== 'true') { nothingYet(); return; }

    var ident = getIdentity();
    if (!ident || !ident.email) { nothingYet(); return; }

    var base = (conf(root, 'upload-endpoint', '') || '').replace(/\/upload$/, '');
    if (!base) { nothingYet(); return; }

    /* The read is gated, and being gated is not the same as having
       nothing. Before this, both ended at the same "nothing sent yet"
       band, so a customer whose renders were finished and waiting was
       told, in effect, that they had not sent anything.

       The offer is a button rather than a modal that opens itself.
       Photographs of the inside of someone's home are worth asking for
       a code over, but nobody expects to be asked for one by a page
       they have merely scrolled to. */
    function askToVerify() {
      var canVerify = window.SC && SC.identity && typeof SC.identity.ensureVerified === 'function';
      if (!verify || !canVerify) { nothingYet(); return; }
      hide(awaiting);
      show(verify);
    }

    function read(retried) {
      fetch(base + '?email=' + encodeURIComponent(ident.email), { credentials: 'include' })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (d && d.requiresVerification) {
            if (retried) { nothingYet(); return; }
            askToVerify();
            return;
          }
          fill(d);
        })
        .catch(function () { nothingYet(); });
    }

    if (verifyBtn && !verifyBtn.hasAttribute('data-bound')) {
      verifyBtn.setAttribute('data-bound', '1');
      on(verifyBtn, 'click', function () {
        text(verifyMsg, '');
        verifyBtn.disabled = true;
        SC.identity.ensureVerified({ reason: conf(root, 'verify-reason', '') })
          .then(function () {
            verifyBtn.disabled = false;
            hide(verify);
            /* retried: a second refusal means something is wrong with
               the session rather than with the customer, and asking
               again would only loop them. */
            read(true);
          })
          .catch(function (err) {
            verifyBtn.disabled = false;
            /* A dismissed modal is a decision, not a fault, so it says
               nothing and leaves the button where it was: trying again
               costs one click. Anything else went wrong on our side and
               is worth admitting to. */
            if (err && err.message && err.message !== 'dismissed') {
              text(verifyMsg, conf(root, 'verify-error',
                'We could not send a code just now. Please try again in a moment.'));
            }
          });
      });
    }

    read(false);

    function fill(d) {
        if (!d || !d.renders || !d.renders.length) { nothingYet(); return; }
        if (awaiting) hide(awaiting);
        if (verify) hide(verify);
        var pending = conf(root, 'returned-pending', 'Being prepared now');
        var dl = conf(root, 'returned-download', 'Download');

        grid.innerHTML = d.renders.map(function (r) {
          if (r.status === 'done' && r.imageUrl) {
            return '<figure class="sc-studio-returned-item">' +
              '<a href="' + escapeHtml(r.imageUrl) + '" target="_blank" rel="noopener">' +
                '<img src="' + escapeHtml(r.imageUrl) + '" alt="' + escapeHtml(r.buildLabel || 'Your room') + '" loading="lazy">' +
              '</a>' +
              '<figcaption>' + escapeHtml(r.buildLabel || '') +
                ' <a href="' + escapeHtml(r.imageUrl) + '" download>' + escapeHtml(dl) + '</a>' +
              '</figcaption></figure>';
          }
          return '<div class="sc-studio-returned-item is-pending">' +
            '<span>' + escapeHtml(pending) + '</span>' +
            '<figcaption>' + escapeHtml(r.buildLabel || '') + '</figcaption></div>';
        }).join('');
        show(wrap);
    }
  }


  /* ── Boot ──────────────────────────────────────────── */

  function boot() {
    $$('[data-studio-section="hero"]').forEach(function (root) {
      hydrateHero(root);
      $$('[data-studio-drop]', root).forEach(function (z) { wireDropzone(z, root); });
      /* After the dropzones are wired, because picking a kind opens that
         zone's file dialog and the input has to be listening by then. */
      wireAsk(root);
      reflectLocked(root);
      /* The returned-renders panels used to live in their own section and
         were read by its hydrator. They sit under the upload box now, so
         the read belongs here: leaving it behind would have left them in
         the markup and permanently hidden, which nothing on the page
         would have shown, because renders on the page are off by default. */
      loadReturned(root);
      show(root);
    });
    $$('[data-studio-section="builds"]').forEach(function (root) {
      hydrateBuilds(root); show(root);
    });
    $$('[data-studio-section="fabrics"]').forEach(function (root) { hydrateFabrics(root); renderLookbook(root); });
    $$('[data-studio-section="style"]').forEach(function (root) { hydrateStyle(root); });
    $$('[data-studio-section="fit"]').forEach(function (root) { hydrateFit(root); });
    $$('[data-studio-section="reviews"]').forEach(function (root) { hydrateReviews(root); });
    $$('[data-studio-section="delivery"]').forEach(function (root) { hydrateDelivery(root); });
    $$('[data-studio-section="order"]').forEach(function (root) { hydrateOrder(root); });
    $$('[data-studio-section="door"]').forEach(function (root) { hydrateDoor(root); });
    $$('[data-studio-whatsapp]').forEach(function (el) {
      wireWhatsApp(el.closest('[data-studio-section]') || document);
    });
    applyStage();
    applyIntent();
  }

  /* ══════════════════════════════════════════════════════
     STAGE
     ══════════════════════════════════════════════════════

     Where the customer has got to, derived from what they have done.
     One source of truth: sections declare which stages they belong to
     and the rest follows, rather than each section guessing.

     Two kinds of stage, and the difference matters:

     SOFT (new, saved, imagining, ready) come from localStorage. They
     decide what the page asks for next. A wrong one shows a stale
     prompt, which is a fair price for having no login.

     ORDERED is decided on the server by the sc-studio-stage snippet,
     from Shopify's own record of the customer's orders, and read here
     off data-sc-ordered. It is never written from this file. There is
     no recordEvent('ordered'), by design: an order is the only stage
     that gates anything worth protecting, and a flag the visitor can
     type into a console is not an answer to "has this person paid us".

     A customer who has ordered but is not signed in reads as "ready".
     That is the correct direction to fail in.

     The pre-paint copy of the soft ladder lives in the sc-studio-stage
     snippet. Change both together.                                     */

  var EVENTS_KEY = 'sc_studio_events';
  var STAGES = ['new', 'saved', 'imagining', 'ready', 'ordered'];

  function readEvents() {
    try { return JSON.parse(localStorage.getItem(EVENTS_KEY) || '{}'); }
    catch (e) { return {}; }
  }

  function recordEvent(name) {
    if (!guardEvent(name)) return;
    try {
      var ev = readEvents();
      if (ev[name]) return;          /* first occurrence is the one that matters */
      ev[name] = Date.now();
      localStorage.setItem(EVENTS_KEY, JSON.stringify(ev));
    } catch (e) {}
    applyStage();
  }

  /* Authoritative, server-rendered. Only Shopify can make this true. */
  function hasOrdered() {
    return document.documentElement.getAttribute('data-sc-ordered') === 'true';
  }

  function computeStage() {
    if (hasOrdered()) return 'ordered';
    var ev = readEvents();
    if (ev.samples) return 'ready';
    if (ev.roomUpload) return 'imagining';
    if (getBuilds().length) return 'saved';
    return 'new';
    /* No 'ordered' branch here on purpose. See the note above. */
  }

  /* Sets the attribute the CSS keys off. The snippet has usually done
     this already before paint; this corrects it if anything has changed
     since, and is what keeps the page live after an upload. */
  function applyStage() {
    var stage = computeStage();
    document.documentElement.setAttribute('data-sc-stage', stage);
    return stage;
  }

  /* Guard: recordEvent must never be used to claim an order. Called from
     the console or a stray edit, this keeps the promise the comment above
     makes rather than relying on nobody trying. */
  function guardEvent(name) {
    if (name === 'ordered') {
      if (window.console && console.warn) {
        console.warn('sc-studio: "ordered" is decided server-side and cannot be set from the client.');
      }
      return false;
    }
    return true;
  }

  /* ══════════════════════════════════════════════════════
     TRANCHE 2 — fabrics, style, fit, reviews, delivery, door
     ══════════════════════════════════════════════════════ */

  /* Ranges and fabrics the customer has actually chosen, most recent
     first, de-duplicated. Everything downstream filters on these. */

  function chosenRanges() {
    var seen = {}, out = [];
    getBuilds().forEach(function (b) {
      var r = b.rangeName || b.model || '';
      if (r && !seen[r.toLowerCase()]) { seen[r.toLowerCase()] = 1; out.push(r); }
    });
    return out;
  }

  function chosenFabrics() {
    var seen = {}, out = [];
    getBuilds().forEach(function (b) {
      var f = b.fabricName || b.fabric || '';
      if (f && !seen[f.toLowerCase()]) { seen[f.toLowerCase()] = 1; out.push(f); }
    });
    return out;
  }

  function handleise(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  /* "Coco", "Coco and Henrietta", "Coco, Henrietta and Barton" */
  function joinNaturally(list, conj) {
    conj = conj || 'and';
    if (!list.length) return '';
    if (list.length === 1) return list[0];
    return list.slice(0, -1).join(', ') + ' ' + conj + ' ' + list[list.length - 1];
  }

  function fillHeading(root, el, withData, anonKey, patternKey, map) {
    if (!el) return;
    var tpl = withData ? conf(root, patternKey, '') : conf(root, anonKey, '');
    if (!tpl) return;
    Object.keys(map || {}).forEach(function (k) {
      tpl = tpl.replace(new RegExp('\\{' + k + '\\}', 'gi'), map[k]);
    });
    text(el, tpl);
  }

  /* ── Fabrics ───────────────────────────────────────── */

  /* ── The look book ─────────────────────────────────────

     The fabrics photographed in real rooms, grouped out of the gallery
     dataset at render time. Nothing here is curated by hand: add pins to
     the gallery and the look book follows, which is the same bargain the
     gallery row makes.

     The customer's own fabrics lead when they have chosen any, because a
     person who has picked Sea Salt is better served by nine rooms that
     answer "what does a whole room do around a pale linen" than by a
     parade of what everyone else chose. */
  function renderLookbook(root) {
    var wrap = $('[data-studio-lookbook]', root);
    var strip = $('[data-studio-lookbook-strip]', root);
    if (!wrap || !strip || !window.SC || !SC.gallery || !SC.gallery.items) return;
    var items = SC.gallery.items;
    if (!items.length) return;

    /* Sofa ranges only, so table props and bed linen never present as
       sofa fabrics. Cocobella is absent from this list on purpose: it is
       a John Lewis exclusive, so its pins neither count nor show. */
    var SOFA_RANGES = { Coco: 1, Barton: 1, Henrietta: 1, Felix: 1, Isobel: 1, Sophia: 1 };

    var groups = {};
    items.forEach(function (p) {
      if (!p.fabric || !SOFA_RANGES[p.range]) return;
      if (/own material|own fabric/i.test(p.fabric)) return;
      (groups[p.fabric] = groups[p.fabric] || []).push(p);
    });

    /* Swatch colours come from the configurator's fabric list where the
       names line up. A designer fabric the configurator does not carry
       still gets a card, just without the swatch dot. */
    var swatches = {};
    if (SC.fabrics) {
      Object.keys(SC.fabrics).forEach(function (h) {
        var f = SC.fabrics[h];
        if (f && f.name && f.swatchColour) swatches[f.name.toLowerCase()] = f.swatchColour;
      });
    }

    var chosen = {};
    getBuilds().forEach(function (b) {
      var n = (b.fabricName || '').toLowerCase();
      if (n) chosen[n] = 1;
    });

    var fabrics = Object.keys(groups).map(function (name) {
      var pins = groups[name];
      /* Our photography fronts the card; a customer photograph only when
         it is all a fabric has. */
      var ours = pins.filter(function (p) { return !p.customerImage; });
      var ordered = ours.concat(pins.filter(function (p) { return p.customerImage; }));
      return {
        name: name,
        count: pins.length,
        pin: ordered[0],
        /* Spares, because a pin can point at a file the CDN does not have:
           Wildwood Ivory's lead image is a 404 today and rendered as a
           broken frame in the middle of the strip. The card falls through
           to the next photograph of the same fabric on error, and only
           removes itself when the fabric has no working picture at all. */
        alts: ordered.slice(1, 4).map(function (p) { return p.image; }),
        mine: chosen[name.toLowerCase()] ? 1 : 0,
        swatch: swatches[name.toLowerCase()] || ''
      };
    }).filter(function (f) { return f.count >= 2; });

    fabrics.sort(function (a, b) { return (b.mine - a.mine) || (b.count - a.count); });

    var max = parseInt(wrap.getAttribute('data-lookbook-count'), 10) || 10;
    fabrics = fabrics.slice(0, max);
    /* A look book of two reads as a shelf gap, so below four the band
       stays hidden and the section is no worse than it was. */
    if (fabrics.length < 4) return;

    strip.innerHTML = fabrics.map(function (f) {
      /* Deep-links to the photograph on the card, which is what the
         gallery can actually do: #image-<id> scrolls to that pin and opens
         its lightbox. There is no fabric filter to link to. The gallery
         filters on range chips only, and selectFilter refuses any key that
         has no chip, so a ?fabric= link would silently land on the
         unfiltered gallery. The card no longer implies otherwise. */
      return '<a class="sc-gt-card sc-studio-lb-card" href="/pages/gallery#image-' + encodeURIComponent(f.pin.id) + '">'
        + '<div class="sc-gt-card-img-wrap">'
        +   '<img class="sc-gt-card-img" src="' + escapeHtml(f.pin.image) + '"'
        +        ' alt="' + escapeHtml(f.name + ' in a real room') + '" loading="lazy"'
        +        ' data-alts="' + escapeHtml(f.alts.join(' ')) + '">'
        +   (f.swatch ? '<span class="sc-studio-lb-swatch" style="background:' + escapeHtml(f.swatch) + '" aria-hidden="true"></span>' : '')
        +   (f.mine ? '<span class="sc-studio-lb-yours">Your fabric</span>' : '')
        + '</div>'
        + '<div class="sc-gt-card-name">' + escapeHtml(f.name) + '</div>'
        + '<div class="sc-studio-lb-count">Seen in ' + f.count + ' rooms</div>'
        + '</a>';
    }).join('');

    /* A pin can point at a file the CDN does not have: Wildwood Ivory's
       lead image is a 404 today and rendered as a broken frame in the
       middle of the strip. Fall through to the next photograph of the same
       fabric, and only drop the card when none of them load.

       Capturing, because error events do not bubble. Bound once per paint,
       on the strip rather than per image. */
    if (!strip.dataset.errBound) {
      strip.dataset.errBound = '1';
      strip.addEventListener('error', function (e) {
        var img = e.target;
        if (!img || img.tagName !== 'IMG') return;
        var alts = (img.getAttribute('data-alts') || '').split(' ').filter(Boolean);
        if (alts.length) {
          img.setAttribute('data-alts', alts.slice(1).join(' '));
          img.src = alts[0];
          return;
        }
        var card = img.closest('.sc-studio-lb-card');
        if (card) card.remove();
      }, true);
    }
    show(wrap);
  }

  function hydrateFabrics(root) {
    var grid = $('[data-studio-fabric-grid]', root);
    var fabrics = chosenFabrics();
    var link = conf(root, 'fabric-link', '');
    var note = conf(root, 'fabric-note', 'The fabric you chose');

    /* Put the customer's own fabrics in front of the CMS tiles. Name,
       swatch colour and image all come from data the theme already
       holds, so a fabric with no film still gets a proper tile. */
    if (grid && fabrics.length) {
      var html = fabrics.map(function (name) {
        var key = handleise(name);
        var meta = (SC.fabrics && SC.fabrics[key]) || null;
        var img = (SC.fabricImages && SC.fabricImages[key]) || '';
        var swatch = meta && meta.swatchColour
          ? '<span class="sc-studio-swatch" style="background:' + escapeHtml(meta.swatchColour) + '"></span>' : '';
        var inner =
          '<div class="sc-studio-fabric-thumb is-contain">' +
            (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(name) + '" loading="lazy">' : '') +
            (link ? '<span class="sc-studio-play"></span>' : '') +
          '</div>' +
          '<div class="sc-studio-fabric-body">' +
            '<h4>' + swatch + escapeHtml(name) + '</h4>' +
            '<p>' + escapeHtml(meta && meta.composition ? meta.composition : note) + '</p>' +
          '</div>';
        return link
          ? '<a class="sc-studio-fabric-card" href="' + escapeHtml(link) + '">' + inner + '</a>'
          : '<div class="sc-studio-fabric-card is-static">' + inner + '</div>';
      }).join('');
      grid.insertAdjacentHTML('afterbegin', html);
    }

    /* Sample bar */
    var lede = $('[data-studio-sample-lede]', root);
    if (lede && fabrics.length) {
      var personal = conf(root, 'sample-lede-personal', '');
      if (personal) text(lede, personal + ' ' + joinNaturally(fabrics) + '.');
    }

    var btn = $('[data-studio-samples]', root);
    var status = $('[data-studio-sample-status]', root);
    on(btn, 'click', function () {
      var endpoint = conf(root, 'sample-endpoint', '');
      var pageUrl = conf(root, 'sample-url', '/pages/fabric-samples');
      var ident = getIdentity() || {};

      /* No endpoint, or nobody we can post for: send them to the
         samples page with the fabrics pre-filled rather than failing. */
      if (!endpoint || !ident.email) {
        recordEvent('samples');
        window.location.href = pageUrl + (fabrics.length ? '?fabric=' + encodeURIComponent(fabrics.join(',')) : '');
        return;
      }

      btn.disabled = true;
      if (status) { status.textContent = conf(root, 'sample-pending', 'Sending your request\u2026'); status.classList.remove('is-error'); }
      fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: ident.email, firstName: ident.firstName || '',
          fabrics: fabrics, pageUrl: window.location.href
        })
      }).then(function (r) { return r.json(); }).then(function (d) {
        if (!d || d.ok === false) throw new Error((d && d.error) || 'Request failed');
        if (status) status.textContent = conf(root, 'sample-done', 'On their way.');
        btn.hidden = true;
        recordEvent('samples');
      }).catch(function () {
        /* Never dead-end the customer: fall through to the page. */
        window.location.href = pageUrl + (fabrics.length ? '?fabric=' + encodeURIComponent(fabrics.join(',')) : '');
      });
    });
  }

  /* ── Style ─────────────────────────────────────────────
     SC.saves holds builds and favourites only, so the style choice
     lives in localStorage for now. When a taste endpoint exists,
     saveStyle is the single place to wire it up.                    */

  var STYLE_KEY = 'sc_studio_style';

  function savedStyle() {
    try { return localStorage.getItem(STYLE_KEY) || ''; } catch (e) { return ''; }
  }
  function saveStyle(name) {
    try { localStorage.setItem(STYLE_KEY, name); } catch (e) {}
  }

  function hydrateStyle(root) {
    var reveal = $('[data-studio-style-reveal]', root);
    var title = $('[data-studio-style-title]', root);
    var sub = $('[data-studio-style-sub]', root);
    var tiles = $$('[data-studio-style]', root);
    var ranges = chosenRanges();

    function apply(name, persist) {
      if (!name) return;
      if (persist) saveStyle(name);
      tiles.forEach(function (t) {
        t.setAttribute('aria-pressed', String(t.getAttribute('data-style') === name));
      });
      if (title) text(title, conf(root, 'reveal-heading', '{style}').replace(/\{style\}/gi, name));
      if (sub) {
        text(sub, conf(root, 'reveal-lede', '')
          .replace(/\{style\}/gi, name)
          .replace(/\{range\}/gi, ranges.length ? joinNaturally(ranges) : 'sofa'));
      }
      /* Show only the rooms tagged for this style, or untagged ones. */
      $$('.sc-studio-room-tile', root).forEach(function (tile) {
        var forStyle = tile.getAttribute('data-style-for') || '';
        tile.hidden = !!(forStyle && forStyle !== name);
      });
      show(reveal);
    }

    tiles.forEach(function (t) {
      on(t, 'click', function () { apply(t.getAttribute('data-style'), true); });
    });

    apply(savedStyle(), false);
  }

  /* ── Will it fit ───────────────────────────────────────
     Built from SC.dimensions, which holds real per-size values keyed
     by legend code. Nothing is invented: an unknown range or size
     hides the section rather than guessing at measurements.          */

  function hydrateFit(root) {
    var body = $('[data-studio-dim-body]', root);
    if (!body || !SC.dimensions) { hide(root); return; }

    var builds = getBuilds();
    var b = builds.length ? builds[0] : null;
    var rangeKey = handleise((b && (b.rangeName || b.model)) || conf(root, 'fallback-range', 'coco'));
    var sizeName = (b && (b.sizeName || b.size)) || conf(root, 'fallback-size', '3 Seater');

    var dims = SC.dimensions[rangeKey];
    if (!dims || !dims.sizes) { hide(root); return; }

    /* Match the size loosely: "3.5 Seater", "3.5-seater" and
       "3.5 seater sofa" should all find the same row. */
    var target = handleise(sizeName);
    var size = null;
    dims.sizes.forEach(function (s) {
      var h = handleise(s.name);
      if (!size && (h === target || target.indexOf(h) === 0 || h.indexOf(target) === 0)) size = s;
    });
    if (!size) size = dims.sizes[0];
    if (!size) { hide(root); return; }

    var legend = {};
    (dims.legend || []).forEach(function (l) { legend[l.code] = l.label; });

    var codes = conf(root, 'rows', 'A,C,B').split(',').map(function (c) { return c.trim(); });
    var rows = codes.map(function (code) {
      var v = size.values ? size.values[code] : null;
      if (v == null) return '';
      return '<tr><td>' + escapeHtml(legend[code] || code) + '</td><td>' + escapeHtml(v) + ' cm</td></tr>';
    }).join('');

    if (!rows) { hide(root); return; }
    body.innerHTML = rows;

    var label = ((b && (b.rangeName || b.model)) || rangeKey) + ' ' + size.name;
    fillHeading(root, $('[data-studio-fit-heading]', root), !!b,
      'heading-anon', 'heading-pattern', { build: label.trim() });
  }

  /* ── Reviews ───────────────────────────────────────────
     The cards are rendered server-side by the shared sc-review-cards
     snippet, so they already carry real ratings and the site's own
     markup. This only reorders what is there: reviews whose product
     line mentions a range the customer has saved float to the front,
     then the list is trimmed to the visible count.

     Reordering rather than rebuilding means a JS failure still leaves a
     perfectly good reviews block on the page, and card markup stays in
     exactly one place for the whole site.                             */

  function hydrateReviews(root) {
    var grid = $('[data-studio-review-grid]', root);
    if (!grid) return;

    var cards = $$('.sc-review', grid);
    if (!cards.length) return;

    var ranges = chosenRanges();
    var visible = Number(conf(root, 'visible', 3)) || 3;
    var personalise = conf(root, 'personalise', 'true') === 'true';

    fillHeading(root, $('[data-studio-reviews-heading]', root), ranges.length,
      'heading-anon', 'heading-pattern', { ranges: joinNaturally(ranges) });

    if (personalise && ranges.length) {
      /* Whole words, not substrings.

         indexOf matched any range name inside a longer one, so a customer
         with a Coco was shown Cocobella reviews: "cocobella".indexOf("coco")
         is 0. Cocobella and Salthrop are John Lewis exclusives and must
         never surface on an SC page, and beyond the house rule it is simply
         the wrong sofa. Matching on word boundaries keeps Coco to Coco
         while still catching "Coco 3 Seater" and "our Coco, in Chalk". */
      var lower = ranges.map(function (r) { return r.toLowerCase(); });
      var patterns = lower.map(function (r) {
        /* Stripped rather than escaped: a range name is a plain word, and
           dropping anything that is not a letter, digit or space means no
           regex metacharacter can reach the pattern. */
        var safe = r.replace(/[^a-z0-9 ]+/g, '').trim();
        if (!safe) return null;
        return new RegExp('(^|[^a-z0-9])' + safe + '([^a-z0-9]|$)', 'i');
      }).filter(Boolean);
      var hit = [], miss = [];
      cards.forEach(function (card) {
        var product = $('.product', card);
        var hay = (product ? product.textContent : card.textContent).toLowerCase();
        (patterns.some(function (re) { return re.test(hay); }) ? hit : miss).push(card);
      });
      /* Only reorder when it would change something, so the DOM is left
         alone on a page where nothing matches. */
      if (hit.length && hit.length < cards.length) {
        cards = hit.concat(miss);
        cards.forEach(function (card) { grid.appendChild(card); });
      }
    }

    cards.forEach(function (card, i) { card.hidden = i >= visible; });
  }

  /* ── Delivery ──────────────────────────────────────── */

  function hydrateDelivery(root) {
    if (conf(root, 'use-lead-time', 'true') !== 'true') return;
    if (!SC.leadTime) return;
    var market = (window.SC_COUNTRY || 'GB').toUpperCase() === 'US' ? 'us' : 'uk';
    var copy = SC.leadTime[market];
    if (copy) text($('[data-studio-lead-time]', root), copy);
  }

  /* ── Human door ────────────────────────────────────────
     Opens the site's existing chat panel rather than starting a second
     one, and hands it the page context first so "we can already see
     everything you have chosen" is true when someone picks up.        */

  function hydrateDoor(root) {
    var btn = $('[data-studio-chat]', root);
    var status = $('[data-studio-door-status]', root);
    if (!btn) return;

    on(btn, 'click', function () {
      var ident = getIdentity() || {};
      var context = {
        source: 'sofa-studio',
        pageUrl: window.location.href,
        email: ident.email || '',
        firstName: ident.firstName || '',
        style: savedStyle(),
        ranges: chosenRanges(),
        fabrics: chosenFabrics(),
        builds: getBuilds().map(function (b) {
          return {
            id: b.id || '',
            range: b.rangeName || b.model || '',
            size: b.sizeName || b.size || '',
            fabric: b.fabricName || b.fabric || '',
            price: b.price || null
          };
        })
      };
      try { window.SC_STUDIO_CONTEXT = context; } catch (e) {}

      if (window.SCNadia && typeof window.SCNadia.open === 'function') {
        window.SCNadia.open();
      } else if (status) {
        status.textContent = 'Our chat is just waking up. Try again in a moment, or use Book a call.';
      }
    });
  }

  /* ══════════════════════════════════════════════════════
     INTENT
     ══════════════════════════════════════════════════════

     A second axis, independent of stage. Stage is where they are in the
     journey; intent is how strong a signal they have given. Someone can
     be early-stage and high-intent, or late-stage and idle.

     Three tiers: browsing, warm, hot.

     ── What this is and is not for ──

     Intent is a MERCHANDISING decision, not an authorisation one. It is
     computed in the browser and anyone can edit it. That is fine for
     deciding whether to offer a call, and never acceptable for deciding
     whether to show a render, an address or a balance. Those go behind
     SC.identity / requireAuth. If a future feature is tempted to gate
     real data on data-sc-intent, that is the signal it belongs in auth.

     ── Which signals ──

     ── Budget comes from the quiz, not from the build price ──

     Build price measures what somebody CONFIGURED, not what they can
     SPEND. A curious visitor can put a corner unit in the dearest linen
     out of interest; a committed buyer can configure a 2-seater in a
     plain weave. Price tracks size and fabric far more closely than it
     tracks budget, so it is not used for budget at all here.

     The signal is the declared band from the last step of the sofa build
     quiz: under-3k, 3k-5k, 5k-8k, 8k-plus, not-sure. The fabric picker
     uses a shorter set (under-3k, 3k-5k, 5k-plus), so both are mapped.

     "not sure" is treated as unknown rather than low. It usually means
     someone has not priced a handmade sofa before, which is a different
     thing from a small budget, and scoring it as low would quietly
     exclude first-time buyers.

     Declared timeline decays: "6 to 12 months" said in January means
     something different in September.                                   */

  var INTENT_KEY = 'sc_studio_intent';   /* optional override */
  var TIMELINE_DECAY_DAYS = 120;

  /* The sofa build quiz already writes sc_taste on completion
     (sc-catalogue-request-b.liquid:1072) with model, size, fabrics and
     budget, so budget needs no new plumbing at all.

     Timeline is collected in step 1 of the same quiz but is not written
     to sc_taste today. Adding `timeline: leadTimeline` to that object is
     a one-line change and is all this needs. Until then setDeclared can
     supply it, and its absence simply scores nothing. */
  function readDeclared() {
    var out = {};
    try {
      var taste = JSON.parse(localStorage.getItem('sc_taste') || '{}') || {};
      if (taste.budget) out.budget = taste.budget;
      if (taste.timeline) out.timeline = taste.timeline;
      if (taste.purchaseTimeline) out.timeline = taste.purchaseTimeline;
      if (taste.savedAt) out.at = Date.parse(taste.savedAt) || 0;
    } catch (e) {}
    try {
      var override = JSON.parse(localStorage.getItem(INTENT_KEY) || '{}') || {};
      Object.keys(override).forEach(function (k) { out[k] = override[k]; });
    } catch (e) {}
    return out;
  }

  /* Public, so the quiz or any lead form can feed the Studio without
     needing to know how scoring works. */
  function setDeclared(data) {
    try {
      var cur = readDeclared();
      var next = {};
      Object.keys(cur).forEach(function (k) { next[k] = cur[k]; });
      Object.keys(data || {}).forEach(function (k) { next[k] = data[k]; });
      next.at = Date.now();
      localStorage.setItem(INTENT_KEY, JSON.stringify(next));
    } catch (e) {}
    applyIntent();
  }

  /* Budget bands from the sofa build quiz (sc-catalogue-request-b).
     Mapped to the four codes the theme already uses for its email
     snippets at sc-catalogue-request-b.liquid:1106, so the Studio and
     the emails share one vocabulary:

       under_5k   Under 5,000 GBP / Under 7,000 USD
       mid        5,000 to 10,000 / 7,000 to 12,000
       high       10,000+ / 12,000+
       rns        Rather not say

     The stored values are human strings, market-aware, and separated by
     an en-dash. Matched by shape rather than by exact string, so a
     currency or wording change cannot silently drop every customer into
     "not stated". */

  function budgetBand(raw) {
    var v = String(raw || '').toLowerCase().trim();
    if (!v) return null;
    if (/rather not|prefer not|not sure|unsure/.test(v)) return 'rns';
    if (/^under\b/.test(v)) return 'under_5k';
    if (/\+\s*$/.test(v)) return 'high';
    if (/[\u2013\u2014-]/.test(v)) return 'mid';
    return 'rns';
  }

  /* Timeline bands from step 1 of the same quiz. Codes match the
     timelineMap at sc-catalogue-request-b.liquid:1096. */
  function timelineBand(raw) {
    var v = String(raw || '').toLowerCase().trim();
    if (!v) return null;
    if (/as soon as possible|asap/.test(v)) return 'asap';
    if (/within 3|w3m/.test(v)) return 'w3m';
    if (/3 to 6|3_6/.test(v)) return '3_6';
    if (/6 to 12|6_12/.test(v)) return '6_12';
    if (/researching/.test(v)) return 'researching';
    return null;
  }

  function timelineScore(declared, cfg) {
    var code = timelineBand(declared.timeline);
    if (!code) return 0;
    var score = 0;
    if (code === 'asap' || code === 'w3m') score = cfg.timelineNear;
    else if (code === '3_6') score = cfg.timelineMid;
    else if (code === '6_12') score = cfg.timelineFar;
    else score = 0;                        /* just researching */

    /* Decay: a near-term answer given months ago is no longer near-term. */
    var age = declared.at ? (Date.now() - declared.at) / 86400000 : 0;
    if (age > TIMELINE_DECAY_DAYS) score = Math.round(score / 2);
    return score;
  }

  function computeIntent(root) {
    var cfg = intentConfig(root);
    var declared = readDeclared();
    var ev = readEvents();
    var builds = getBuilds();
    var score = 0;
    var why = [];

    var band = budgetBand(declared.budget);
    if (band === 'high') { score += cfg.pointsBudgetHigh; why.push('budget'); }
    else if (band === 'mid') { score += cfg.pointsBudgetMid; why.push('budget'); }
    else if (band === 'under_5k') { score += cfg.pointsBudgetUnder; why.push('budget'); }
    else if (band === 'rns') { score += cfg.pointsBudgetUnknown; why.push('budget not stated'); }

    if (builds.length >= 1) { score += cfg.pointsHasBuild; why.push('saved a build'); }
    if (builds.length >= 2) { score += cfg.pointsMultipleBuilds; why.push('several builds'); }
    if (ev.roomUpload) { score += cfg.pointsRoomUpload; why.push('room photo'); }
    if (ev.samples) { score += cfg.pointsSamples; why.push('samples'); }
    if (savedStyle()) { score += cfg.pointsStyle; why.push('style chosen'); }

    var t = timelineScore(declared, cfg);
    if (t) { score += t; why.push('timeline'); }

    var tier = 'browsing';
    if (score >= cfg.hotAt) tier = 'hot';
    else if (score >= cfg.warmAt) tier = 'warm';

    return { tier: tier, score: score, reasons: why };
  }

  function intentConfig(root) {
    /* Read from whichever Studio section carries the config, so the
       thresholds live in theme settings rather than in this file. */
    var el = root || document.querySelector('[data-studio-intent-config]');
    function n(key, dflt) {
      if (!el) return dflt;
      var v = el.getAttribute('data-' + key);
      return v === null || v === '' ? dflt : Number(v);
    }
    return {
      pointsBudgetHigh: n('pts-budget-high', 3),
      pointsBudgetMid: n('pts-budget-mid', 2),
      pointsBudgetUnder: n('pts-budget-under', 0),
      pointsBudgetUnknown: n('pts-budget-unknown', 1),
      pointsHasBuild: n('pts-has-build', 1),
      /* 2, not 1, so that two saved builds reach warm (1 + 2 = warmAt) on
         their own. Configuring two sofas is the strongest signal short of
         an upload, and it used to score the same as answering one quiz
         question, which left exactly those customers staring at the locked
         teaser. The stage snippet scores the same way: change both. */
      pointsMultipleBuilds: n('pts-multi', 2),
      pointsRoomUpload: n('pts-room', 2),
      pointsSamples: n('pts-samples', 2),
      pointsStyle: n('pts-style', 1),
      timelineNear: n('tl-near', 3),
      timelineMid: n('tl-mid', 1),
      timelineFar: n('tl-far', 0),
      warmAt: n('warm-at', 3),
      hotAt: n('hot-at', 6)
    };
  }

  function applyIntent() {
    var result = computeIntent(null);
    document.documentElement.setAttribute('data-sc-intent', result.tier);
    return result;
  }

  /* ── WhatsApp ──────────────────────────────────────────
     One way to reach us among several, not the recommended one. Plenty
     of customers do not use WhatsApp, and a page that steers everyone
     towards it quietly excludes them. The upload above is the main
     route; this is here for people who would rather send a photo the
     way they send every other photo.

     It does happen to convert HEIC to JPEG on the way, which is useful,
     but that is a reason to keep it available rather than to promote it.

     The message is prefilled with a short reference derived from the
     customer's email, so a photo arriving this way can be matched to the
     same person as one arriving through the upload, without asking them
     for anything. Same derivation as referenceFor() in renders.js. */

  var REF_ALPHABET = 'ACDEFGHJKLMNPQRTUVWXY349';   /* no O/0, I/1, S/5, B/8 */

  function referenceFor(email) {
    var e = 'sc-ref:' + String(email || '').trim().toLowerCase();
    if (!window.crypto || !crypto.subtle) return Promise.resolve('');
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(e)).then(function (d) {
      var b = new Uint8Array(d), out = '';
      for (var i = 0; i < 4; i++) out += REF_ALPHABET[b[i] % REF_ALPHABET.length];
      return 'SC-' + out;
    }).catch(function () { return ''; });
  }

  function whatsappNumber(root) {
    var market = (window.SC_COUNTRY || 'GB').toUpperCase();
    var us = conf(root, 'whatsapp-us', '');
    var uk = conf(root, 'whatsapp-uk', '');
    return (market === 'US' && us) ? us : (uk || us);
  }

  function wireWhatsApp(root) {
    $$('[data-studio-whatsapp]', root).forEach(function (el) {
      var number = whatsappNumber(root);
      if (!number) { hide(el); return; }

      var ident = getIdentity() || {};
      var builds = getBuilds();
      var b = builds.length ? builds[0] : null;
      var label = b
        ? [(b.rangeName || b.model || ''), (b.sizeName || ''), (b.fabricName || b.fabric || '')].filter(Boolean).join(' ')
        : '';

      referenceFor(ident.email).then(function (ref) {
        var tpl = el.getAttribute('data-message') || 'Hello, I would like to send a photo of my room.';
        var msg = tpl
          .replace(/\{build\}/gi, label || 'my sofa')
          .replace(/\{ref\}/gi, ref || '')
          .replace(/\s{2,}/g, ' ')
          .trim();
        el.href = 'https://wa.me/' + number.replace(/[^0-9]/g, '') +
                  '?text=' + encodeURIComponent(msg);
        show(el);
      });
    });
  }

  /* ── Order ─────────────────────────────────────────────
     The money split is worked out from the customer's own build price
     and the deposit percentage, so there is one figure to change and no
     hard-coded amounts anywhere in the markup.

     The mechanism is deliberately swappable. Nothing here assumes the
     draft order route exists yet, and a failure never leaves the
     customer thinking they may have been charged.                     */

  function hydrateOrder(root) {
    var buildEl = $('[data-studio-order-build]', root);
    var money = $('[data-studio-order-money]', root);
    var cta = $('[data-studio-order-cta]', root);
    var status = $('[data-studio-order-status]', root);

    var builds = getBuilds();
    var b = builds.length ? builds[0] : null;
    if (!b) return;                       /* fallback copy already on the page */

    var label = [(b.rangeName || b.model || ''), (b.sizeName || b.size || ''), (b.fabricName || b.fabric || '')]
      .filter(Boolean).join(' \u00B7 ');
    if (buildEl && label) text(buildEl, label);

    var pct = Number(conf(root, 'deposit-percent', 50));
    var total = Number(b.price) || 0;

    if (total > 0 && money) {
      var deposit = Math.round(total * pct / 100);
      text($('[data-studio-order-total]', root), money_(total, b.currency));
      text($('[data-studio-order-deposit]', root), money_(deposit, b.currency));
      text($('[data-studio-order-balance]', root), money_(total - deposit, b.currency));
      show(money);
    }

    /* Re-price before showing a number anyone might act on. A stale
       figure here is worse than a stale figure on a card. */
    if (SC.pricingApi && typeof SC.pricingApi.getPrice === 'function' && b.config) {
      SC.pricingApi.getPrice(b.config).then(function (res) {
        if (!res || !res.price || !money) return;
        var t = Number(res.price);
        var d = Math.round(t * pct / 100);
        text($('[data-studio-order-total]', root), money_(t, res.currency));
        text($('[data-studio-order-deposit]', root), money_(d, res.currency));
        text($('[data-studio-order-balance]', root), money_(t - d, res.currency));

        /* At 100 percent there is no split, and printing "to pay now"
           against a balance of zero invents a stage that does not exist.
           The deposit row is the one kept, because it is the primary row
           and the figure actually being asked for. The total repeats it
           and the balance is nothing, so both go.

           closest() rather than a row query, because the figures are the
           elements this function already holds and their row is simply
           their parent. */
        if (d >= t) {
          var totalCell = $('[data-studio-order-total]', root);
          var balCell = $('[data-studio-order-balance]', root);
          if (totalCell && totalCell.closest) hide(totalCell.closest('.sc-studio-order-row'));
          if (balCell && balCell.closest) hide(balCell.closest('.sc-studio-order-row'));
          var depLabel = $('[data-studio-deposit-label]', root);
          var totLabel = $('[data-studio-total-label]', root);
          if (depLabel && totLabel) text(depLabel, totLabel.textContent);
        }
        show(money);
      }).catch(function () {});
    }

    on(cta, 'click', function () {
      var mode = conf(root, 'mode', 'link');

      if (mode === 'link') {
        var url = conf(root, 'cta-url', '');
        if (url) window.location.href = url;
        return;
      }

      if (mode === 'cart' && b.variantId) {
        cta.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: b.variantId, quantity: 1 }] })
        }).then(function (r) {
          if (!r.ok) throw new Error('cart');
          window.location.href = '/checkout';
        }).catch(function () {
          cta.disabled = false;
          if (status) { status.textContent = conf(root, 'error', 'Something went wrong.'); status.classList.add('is-error'); }
        });
        return;
      }

      var endpoint = conf(root, 'endpoint', '');
      if (!endpoint) {
        var fallback = conf(root, 'cta-url', '');
        if (fallback) { window.location.href = fallback; return; }
        if (status) { status.textContent = conf(root, 'error', 'Something went wrong.'); status.classList.add('is-error'); }
        return;
      }

      var ident = getIdentity() || {};
      cta.disabled = true;
      if (status) { status.textContent = conf(root, 'pending', 'Setting up your order\u2026'); status.classList.remove('is-error'); }

      /* Flat, not { build: b }.

         The draft order route reads price, range and size from the top
         level and refuses anything else with a 422, so the nested shape
         this used to send could never have raised an order. Spelling the
         fields out here also means only what the order needs is sent,
         rather than whatever a saved build happens to carry.

         Full price, deliberately. The route takes one figure and charges
         it, and the configurator quotes the whole sofa, so this matches
         what the customer was shown. Where a deposit is agreed, the sales
         team arranges the split against the order by hand. depositPercent
         still travels for the ops note, but it does not reduce the charge. */
      fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price: Number(b.price) || 0,
          range: b.range || '',
          rangeName: b.rangeName || '',
          size: b.size || '',
          sizeLabel: b.sizeLabel || b.size || '',
          fabric: b.fabric || '',
          fabricName: b.fabricName || '',
          cushion: b.cushion || '',
          cover: b.cover || '',
          piping: b.piping || '',
          cushionFill: b.cushionFill || '',
          legStyle: b.legStyle || '',
          legFinish: b.legFinish || '',
          frame: b.frame || '',
          sku: b.sku || '',
          image: b.imageUrl || '',
          /* The Worker writes this into the order as "Configuration URL",
             which is how the sales team reopens the exact build somebody
             ordered. pageUrl is where they happened to be standing and is
             no use for that, so both travel. */
          shareUrl: (SC.saves && SC.saves.buildShareUrl) ? SC.saves.buildShareUrl(b) : '',
          email: ident.email || '',
          firstName: ident.firstName || '',
          depositPercent: pct,
          pageUrl: window.location.href
        })
      }).then(function (r) { return r.json(); }).then(function (d) {
        /* checkoutUrl is what the route returns. invoiceUrl is accepted
           too so this keeps working if the route is ever brought into
           line with the name used elsewhere. */
        var pay = d && (d.checkoutUrl || d.invoiceUrl);
        if (!d || d.ok === false || !pay) throw new Error((d && d.error) || 'order');
        /* This is the redirect to a payment page, not a completed order.
           Nothing is recorded here. The stage moves to "ordered" only
           once Shopify says so, on a later visit. */
        window.location.href = pay;
      }).catch(function () {
        cta.disabled = false;
        if (status) { status.textContent = conf(root, 'error', 'Something went wrong.'); status.classList.add('is-error'); }
      });
    });
  }

  SC.studio = {
    boot: boot,
    getIdentity: getIdentity,
    getBuilds: getBuilds,
    getTaste: getTaste,
    getStyle: savedStyle,
    getRanges: chosenRanges,
    getFabrics: chosenFabrics,
    getStage: computeStage,
    hasOrdered: hasOrdered,
    getIntent: function () { return computeIntent(null); },
    setDeclared: setDeclared,
    recordEvent: recordEvent,
    upload: upload,
    referenceFor: referenceFor
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(boot, 0); });
  } else {
    setTimeout(boot, 0);
  }
})();
